/**
 * Facet enums + human-readable labels for the email-campaign audience builder.
 *
 * `RelationshipFacet` classifies a recipient by how it relates to XOP (an
 * existing customer, a CRM prospect, an AI-sourced lead at various stages of
 * qualification, or a standalone manual/contact address with no CRM record).
 *
 * `AudienceFilters` widens the legacy `TargetSelection.filters` shape (which
 * only ever carried `industries`) — see `email-target-selector.tsx`. No money
 * / reseller here — wall-neutral.
 *
 * v2 (select-all-then-uncheck model): a facet no longer gates a static
 * "quality" toggle — instead `exclude` carries the lower-cased emails of
 * people the operator explicitly unchecked out of an otherwise-included
 * population. The backend resolver expands relationship+groups+industries,
 * dedups by lower(email), then drops `exclude` emails.
 */

export type RelationshipFacet =
  | "customer"
  | "prospect"
  | "ai_qualified"
  | "ai_harvested"
  | "ai_sales_lead"
  | "standalone"

/** The six relationship facets in rail display order. */
export const RELATIONSHIP_FACETS: RelationshipFacet[] = [
  "customer",
  "prospect",
  "ai_qualified",
  "ai_harvested",
  "ai_sales_lead",
  "standalone",
]

export interface AudienceFilters {
  /** Relationship-type facets to include. Absent/empty = no relationship filter applied. */
  relationship?: RelationshipFacet[]
  /** Dynamic "by industry" segment — resolved at send/recount time. */
  industries?: string[]
  /** Lower-cased emails the operator unchecked out of an otherwise-included
   * population — dropped by the backend resolver after dedup. */
  exclude?: string[]
}

export const RELATIONSHIP_LABELS: Record<RelationshipFacet, string> = {
  customer: "Customers",
  prospect: "Prospects",
  ai_qualified: "AI-Qualified Leads",
  ai_harvested: "AI-Harvested Contacts",
  ai_sales_lead: "AI Sales Leads",
  standalone: "Standalone Contacts",
}
